import apiService from '@sonifi/wb-core/services/api.service';
import { setPermissions } from '@sonifi/wb-user/store//userSlice';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';

const mapPermission = (permission) => ({
  id: permission.id,
  label: permission.label,
  permission: permission.permission,
  uri: permission.uri,
  children: permission.children,
  childrenMapped: permission.childrenMapped,
});

const createUserPermissionsWithHierarchy = (permissionsRes) => {
  const permissionsList = permissionsRes
    .filter((p) => !!p.label)
    .filter((p) => !p.parents.length && p.children.length)
    .sort((a, b) => a.id - b.id);

  const permissionsResult = new Map();

  const searchPermission = (permission) => {
    if (permission?.children?.length) {
      permission.children.forEach((c) => {
        const currentPermission = permissionsRes.find((p) => p.uri === c);
        if (currentPermission) {
          if (!Object.getOwnPropertyDescriptor(permission, 'childrenMapped')) {
            permission.childrenMapped = [];
          }
          permission.childrenMapped.push(currentPermission);
          searchPermission(currentPermission);
        }
      });
    }
  };

  permissionsList.forEach((permission) => {
    permissionsResult.set(permission.id, permission);
    searchPermission(permission);
  });

  return Array.from(permissionsResult.values());
};

export const createUserPermissionsPlain = ({ permissions, user }) => {
  const permissionsResult = new Map();

  // For every User permission we need to do a recursive function that allows
  // to find into the children permissions
  // and a child permission could have more children and so on...
  const searchPermission = (permission) => {
    if (permission?.children?.length) {
      permission.children.forEach((c) => searchPermission(c));
    } else if (permissions.some((p) => p.uri === permission)) {
      const currentPermission = permissions.find((p) => p.uri === permission);
      permissionsResult.set(permission, mapPermission(currentPermission));
      searchPermission(currentPermission);
    } else {
      permissionsResult.set(permission.uri, mapPermission(permission));
    }
  };

  user.permissions.forEach((permission) => {
    const currentPermission = permissions.find((p) => p.uri === permission);
    permissionsResult.set(permission, mapPermission(currentPermission));
    searchPermission(currentPermission);
  });

  return Array.from(permissionsResult.values());
};

export const createUserPermissions = (user) => async (dispatch, getState) => {
  const currentState = getState();
  let permissionsList = currentState.user.permissions;
  if (!permissionsList) {
    const res = await apiService.get(
      `${ENDPOINTS.PERMISSIONS}?rpp=-1&with_embedded=false`,
    );
    permissionsList = res.data.objects;
    dispatch(
      setPermissions({
        permissions: permissionsList,
        permissionsHierarchy:
          createUserPermissionsWithHierarchy(permissionsList),
      }),
    );
  }
  return createUserPermissionsPlain({ permissions: permissionsList, user });
};

export const userHasPermission = (permission) => (_, getState) => {
  const currentState = getState();
  // NOTE: For some reason `user.permissions` contains *ALL* permissions, not the user's permissions.
  const permissionsList = currentState.user.user.permissionsList;
  return permissionsList.some((p) => p.permission === permission);
};

export const addPermissionByUser = ({ permission, user }) => {
  const currentPermissions = [...user.permissionsList];
  const currentPermission = { ...permission };
  currentPermissions.push(permission);

  const searchPermission = (p) => {
    if (p?.childrenMapped?.length) {
      currentPermissions.push(p);
      p.childrenMapped.forEach((c) => {
        searchPermission(c);
      });
    } else {
      currentPermissions.push(p);
    }
  };

  currentPermission?.childrenMapped?.forEach((p) => {
    searchPermission(p);
  });

  return {
    ...user,
    permissions: currentPermissions.map((p) => p.uri),
    permissionsList: currentPermissions,
  };
};

export const removePermissionByUser = ({ permission, user, permissions }) => {
  let currentPermissions = [...user.permissionsList];
  const currentPermission = { ...permission };

  currentPermissions = currentPermissions.filter(
    (p) => p.id !== currentPermission.id,
  );

  const searchPermission = (p) => {
    if (p?.childrenMapped?.length) {
      p.childrenMapped.forEach((c) => {
        searchPermission(c);
      });
    }
    currentPermissions = currentPermissions.filter((cp) => p.id !== cp.id);
  };

  const searchParentPermission = (p) => {
    currentPermissions = currentPermissions.filter((cp) => p !== cp.uri);
    const permissionFound = permissions.find((pm) => pm.uri === p);
    if (permissionFound?.parents?.length) {
      permissionFound.parents.forEach((c) => {
        searchParentPermission(c);
      });
    }
  };

  currentPermission?.parents?.forEach((p) => {
    searchParentPermission(p);
  });

  currentPermission?.childrenMapped?.forEach((p) => {
    searchPermission(p);
  });

  return {
    ...user,
    permissions: currentPermissions.map((p) => p.uri),
    permissionsList: currentPermissions,
  };
};
